/**
 * useA2AAgent - A stateless React hook wrapper for the @a2a-js/sdk
 *
 * This hook provides a convenient interface to interact with any A2A-compliant agent.
 * It wraps the A2A JS SDK's Client class and provides methods for sending messages,
 * streaming responses, and managing tasks.
 *
 * @see https://github.com/a2aproject/a2a-js for SDK documentation
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
var __await = (this && this.__await) || function (v) { return this instanceof __await ? (this.v = v, this) : new __await(v); }
var __asyncGenerator = (this && this.__asyncGenerator) || function (thisArg, _arguments, generator) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var g = generator.apply(thisArg, _arguments || []), i, q = [];
    return i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i;
    function verb(n) { if (g[n]) i[n] = function (v) { return new Promise(function (a, b) { q.push([n, v, a, b]) > 1 || resume(n, v); }); }; }
    function resume(n, v) { try { step(g[n](v)); } catch (e) { settle(q[0][3], e); } }
    function step(r) { r.value instanceof __await ? Promise.resolve(r.value.v).then(fulfill, reject) : settle(q[0][2], r); }
    function fulfill(value) { resume("next", value); }
    function reject(value) { resume("throw", value); }
    function settle(f, v) { if (f(v), q.shift(), q.length) resume(q[0][0], q[0][1]); }
};
var __asyncDelegator = (this && this.__asyncDelegator) || function (o) {
    var i, p;
    return i = {}, verb("next"), verb("throw", function (e) { throw e; }), verb("return"), i[Symbol.iterator] = function () { return this; }, i;
    function verb(n, f) { i[n] = o[n] ? function (v) { return (p = !p) ? { value: __await(o[n](v)), done: n === "return" } : f ? f(v) : v; } : f; }
};
import { useCallback, useRef } from 'react';
import { ClientFactory, } from '@a2a-js/sdk/client';
import { Message, taskStateToJSON } from '@a2a-js/sdk';
// ============================================================================
// Helper Utilities
// ============================================================================
/**
 * Type guard to check if a response is a Message.
 * A2A v1.0 removed the `kind` discriminator, so these guards test for
 * fields unique to each shape.
 */
export function isMessage(response) {
    return 'messageId' in response;
}
/**
 * Type guard to check if a response is a Task.
 * Task carries `id`; TaskStatusUpdateEvent carries `taskId` instead.
 */
export function isTask(response) {
    return 'id' in response && 'status' in response;
}
/**
 * Type guard for TaskStatusUpdateEvent
 */
export function isStatusUpdateEvent(event) {
    return 'taskId' in event && 'status' in event && !('id' in event);
}
/**
 * Type guard for TaskArtifactUpdateEvent
 */
export function isArtifactUpdateEvent(event) {
    return 'artifact' in event;
}
/**
 * A2A v1.0 streams yield StreamResponse envelopes wrapping the actual payload.
 * Unwrap to the discriminated union consumers expect, skipping empty envelopes.
 */
function unwrapStream(stream) {
    return __asyncGenerator(this, arguments, function* unwrapStream_1() {
        var e_1, _a;
        try {
            for (var stream_1 = __asyncValues(stream), stream_1_1; stream_1_1 = yield __await(stream_1.next()), !stream_1_1.done;) {
                const event = stream_1_1.value;
                if (!event.payload) {
                    continue;
                }
                yield yield __await(event.payload.value);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (stream_1_1 && !stream_1_1.done && (_a = stream_1.return)) yield __await(_a.call(stream_1));
            }
            finally { if (e_1) throw e_1.error; }
        }
    });
}
/**
 * Generate a client-side message id. A2A v1.0 requires messageId to be a
 * string, so the server can no longer be relied upon to mint one.
 */
function generateMessageId() {
    const cryptoRef = globalThis.crypto;
    if (cryptoRef === null || cryptoRef === void 0 ? void 0 : cryptoRef.randomUUID) {
        return cryptoRef.randomUUID();
    }
    return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
/**
 * Helper function to build a Message object for sending to an agent.
 * Message.fromJSON accepts the A2A v1.0 wire shape and produces the runtime
 * representation the SDK transports expect.
 */
export function buildUserMessage(options) {
    var _a, _b, _c;
    return Message.fromJSON({
        messageId: generateMessageId(),
        role: 'ROLE_USER',
        parts: [{ text: options.text }],
        contextId: (_a = options.contextId) !== null && _a !== void 0 ? _a : undefined,
        taskId: (_b = options.taskId) !== null && _b !== void 0 ? _b : undefined,
        metadata: (_c = options.metadata) !== null && _c !== void 0 ? _c : undefined,
    });
}
/**
 * Helper function to build a SendMessageRequest from a simple text message
 */
export function buildMessageParams(text, options) {
    return {
        tenant: '',
        message: buildUserMessage(Object.assign({ text }, options)),
        configuration: undefined,
        metadata: undefined,
    };
}
/**
 * Extract text content from a Message
 */
export function extractMessageText(message) {
    return message.parts
        .map((part) => { var _a; return (((_a = part.content) === null || _a === void 0 ? void 0 : _a.$case) === 'text' ? part.content.value : ''); })
        .filter((text) => text.length > 0)
        .join('\n');
}
/**
 * Extract the final status from a Task
 */
export function getTaskStatus(task) {
    var _a;
    const state = (_a = task.status) === null || _a === void 0 ? void 0 : _a.state;
    return state === undefined ? 'unknown' : taskStateToJSON(state);
}
/** Terminal task states in A2A v1.0 wire form */
export const TERMINAL_TASK_STATES = [
    'TASK_STATE_COMPLETED',
    'TASK_STATE_FAILED',
    'TASK_STATE_CANCELED',
    'TASK_STATE_REJECTED',
];
/**
 * Check if a task is in a terminal state (completed, failed, canceled, rejected)
 */
export function isTaskTerminal(task) {
    return TERMINAL_TASK_STATES.includes(getTaskStatus(task));
}
// ============================================================================
// Client Cache
// ============================================================================
/**
 * Static cache for reusing Client initialisation promises by URL.
 * Prevents duplicate ClientFactory.createFromUrl() calls for the same agent.
 * Failed promises are automatically evicted so the next call retries.
 */
const clientCache = new Map();
/**
 * Get a cache key for the given URL and factory config.
 * The key includes a hash of the factory config to differentiate
 * clients with different configurations for the same URL.
 */
function getCacheKey(url, factoryConfig) {
    var _a, _b;
    if (!factoryConfig) {
        return url;
    }
    // Include serialisable config fields; functions (cardResolver) use presence flags.
    const configHash = JSON.stringify({
        preferredTransports: factoryConfig.preferredTransports,
        transportCount: (_b = (_a = factoryConfig.transports) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0,
        hasCardResolver: !!factoryConfig.cardResolver,
    });
    return `${url}::${configHash}`;
}
/**
 * Get or create a cached client for the given URL.
 * Failed initialisations are evicted from the cache so subsequent
 * calls retry instead of returning a stale rejected promise (C2 fix).
 */
function getOrCreateCachedClient(url, factoryConfig) {
    return __awaiter(this, void 0, void 0, function* () {
        const cacheKey = getCacheKey(url, factoryConfig);
        const cached = clientCache.get(cacheKey);
        if (cached) {
            return cached;
        }
        const factory = new ClientFactory(factoryConfig);
        const promise = factory.createFromUrl(url);
        clientCache.set(cacheKey, promise);
        // Evict on failure so the next caller retries (C2 fix).
        // Uses void side-effect to avoid creating an unhandled rejected promise.
        void promise.catch(() => {
            if (clientCache.get(cacheKey) === promise) {
                clientCache.delete(cacheKey);
            }
        });
        return promise;
    });
}
// ============================================================================
// Hook Implementation
// ============================================================================
/**
 * A stateless React hook for interacting with A2A-compliant agents.
 *
 * This hook provides a wrapper over the @a2a-js/sdk Client class with:
 * - Lazy client initialization
 * - Type-safe method wrappers
 * - Support for both blocking and streaming message patterns
 * - AbortSignal support for request cancellation
 *
 * @example
 * ```tsx
 * // Using with agent URL (recommended)
 * const agent = useA2AAgent({ agentUrl: 'https://my-agent.example.com' });
 *
 * // Send a message
 * const response = await agent.sendMessage(buildMessageParams('Hello!'));
 *
 * // Stream responses
 * for await (const event of agent.sendMessageStream(params)) {
 *   if (isStatusUpdateEvent(event)) {
 *     console.log('Status:', event.status.state);
 *   }
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Using with pre-configured client
 * const client = await new ClientFactory().createFromUrl(url);
 * const agent = useA2AAgent({ client });
 * ```
 */
export function useA2AAgent(options) {
    var _a;
    // Validate options - must have exactly one of agentUrl or client
    if (options.agentUrl && options.client) {
        throw new Error('useA2AAgent: Cannot provide both agentUrl and client. Please provide only one.');
    }
    if (!options.agentUrl && !options.client) {
        throw new Error('useA2AAgent: Must provide either agentUrl or client.');
    }
    // Use ref to store the client instance for lazy initialization
    const clientRef = useRef((_a = options.client) !== null && _a !== void 0 ? _a : null);
    /**
     * Get or create the Client instance (lazy initialization with caching)
     */
    const getClient = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        // If client is already initialized in this hook instance, return it
        if (clientRef.current) {
            return clientRef.current;
        }
        // Start initialization using the cache
        if (!options.agentUrl) {
            throw new Error('useA2AAgent: agentUrl is required for client creation');
        }
        // Use the static cache to get or create the client
        const client = yield getOrCreateCachedClient(options.agentUrl, options.factoryConfig);
        clientRef.current = client;
        return client;
    }), [options.agentUrl, options.factoryConfig]);
    /**
     * Check if the client is ready (initialized)
     */
    const isReady = useCallback(() => {
        return clientRef.current !== null;
    }, []);
    /**
     * Get the agent's metadata card
     */
    const getAgentCard = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const client = yield getClient();
        return client.getAgentCard();
    }), [getClient]);
    /**
     * Send a message to the agent (blocking mode)
     */
    const sendMessage = useCallback((params, requestOptions) => __awaiter(this, void 0, void 0, function* () {
        const client = yield getClient();
        return client.sendMessage(params, {
            signal: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.signal,
            serviceParameters: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.serviceParameters,
            context: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.context,
        });
    }), [getClient]);
    /**
     * Send a message and stream responses
     */
    const sendMessageStream = useCallback(function (params, requestOptions) {
        return __asyncGenerator(this, arguments, function* () {
            const client = yield __await(getClient());
            const stream = client.sendMessageStream(params, {
                signal: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.signal,
                serviceParameters: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.serviceParameters,
                context: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.context,
            });
            yield __await(yield* __asyncDelegator(__asyncValues(unwrapStream(stream))));
        });
    }, [getClient]);
    /**
     * Get a task by ID
     */
    const getTask = useCallback((taskId, requestOptions) => __awaiter(this, void 0, void 0, function* () {
        const client = yield getClient();
        return client.getTask({ tenant: "", id: taskId }, {
            signal: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.signal,
            serviceParameters: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.serviceParameters,
            context: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.context,
        });
    }), [getClient]);
    /**
     * Cancel an active task
     */
    const cancelTask = useCallback((taskId, requestOptions) => __awaiter(this, void 0, void 0, function* () {
        const client = yield getClient();
        return client.cancelTask({ tenant: "", id: taskId, metadata: undefined }, {
            signal: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.signal,
            serviceParameters: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.serviceParameters,
            context: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.context,
        });
    }), [getClient]);
    /**
     * Resubscribe to a task's event stream
     */
    const resubscribeTask = useCallback(function (taskId, requestOptions) {
        return __asyncGenerator(this, arguments, function* () {
            const client = yield __await(getClient());
            const stream = client.resubscribeTask({ tenant: "", id: taskId }, {
                signal: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.signal,
                serviceParameters: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.serviceParameters,
                context: requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.context,
            });
            yield __await(yield* __asyncDelegator(__asyncValues(unwrapStream(stream))));
        });
    }, [getClient]);
    // ============================================================================
    // Push Notification Configuration Methods (Placeholder)
    // ============================================================================
    //
    // The A2A SDK supports push notification configuration for tasks.
    // These methods allow configuring webhooks for task status updates.
    //
    // Methods to implement:
    // - setTaskPushNotificationConfig(config): Configure webhook for a task
    // - getTaskPushNotificationConfig(taskId): Get webhook config for a task
    // - listTaskPushNotificationConfigs(): List all webhook configurations
    // - deleteTaskPushNotificationConfig(taskId): Remove webhook config
    //
    // @see https://github.com/a2aproject/a2a-js - Server Implementation Guide > Push Notifications (4.5)
    // @see Client methods: setTaskPushNotificationConfig, getTaskPushNotificationConfig, etc.
    // ============================================================================
    return {
        getAgentCard,
        sendMessage,
        sendMessageStream,
        getTask,
        cancelTask,
        resubscribeTask,
        isReady,
        getClient,
    };
}
export default useA2AAgent;
